import React from "react";
import PropTypes from "prop-types";
import { Navigate } from "react-router-dom";
import authService from "../services/auth";
import { ROUTES } from "../config/constants";
import { getSafeReturnPath } from "../utils/navigation";

// Stale-token cleanup is handled by the startup useEffect in App.js.
// isAuthenticated() is a pure predicate — safe to call during render.

/**
 * Wraps public-only pages (landing, login, register, etc.).
 * Authenticated users are redirected to `redirectTo` — keeping routing
 * policy in the router layer rather than inside page components.
 *
 * @param {React.ReactNode} children - Page content shown to unauthenticated users.
 * @param {string} [redirectTo] - Destination for authenticated users.
 *   Defaults to ROUTES.AUTHENTICATED_HOME ("/dashboard").  Override only when
 *   a specific public page needs a different post-auth destination.
 *   Must be a same-origin relative path (starts with "/" but not "//").
 *   Unsafe values fall back to ROUTES.AUTHENTICATED_HOME.
 */
export function PublicRoute({
  children,
  redirectTo = ROUTES.AUTHENTICATED_HOME,
}) {
  if (authService.isAuthenticated()) {
    // getSafeReturnPath rejects protocol-relative and absolute URLs, keeping
    // the redirect same-origin even if a future caller passes user data here.
    return <Navigate to={getSafeReturnPath(redirectTo)} replace />;
  }
  return children;
}

// Custom validator: redirectTo must be a same-origin relative path.
// getSafeReturnPath() enforces this at runtime, but a PropTypes validator
// surfaces misuse as a console.error in development — before the silent
// fallback fires and a developer wonders why their redirectTo was ignored.
function relativePath(props, propName, componentName) {
  const val = props[propName];
  if (val === undefined) return null;
  if (typeof val !== "string") {
    return new Error(
      `${componentName}: ${propName} must be a string, got ${typeof val}`,
    );
  }
  if (!val.startsWith("/") || val.startsWith("//")) {
    return new Error(
      `${componentName}: ${propName} must be a relative path starting with "/" ` +
        `(protocol-relative and absolute URLs are rejected). Got: "${val}"`,
    );
  }
  return null;
}

PublicRoute.propTypes = {
  children: PropTypes.node.isRequired,
  redirectTo: relativePath,
};
